<template>
    <div>
        <!-- Page Header -->
        <section class="bg-dark pt-5 pb-4">
            <div class="container py-5">
                <h1 class="text-light pt-1 pt-md-3 mb-4">
                    Integrations
                </h1>
                <p class="text-light opacity-75 mb-0">
                    Connect your accounts to streamline your workflow
                </p>
            </div>
        </section>
        <!-- / Page Header -->

        <!-- Integrations Content -->
        <section class="position-relative bg-white rounded-xxl-4 zindex-5" style="margin-top: -30px;">
            <div class="container pt-4 pb-5 mb-md-4">
                <!-- Breadcrumb-->
                <breadcrumb :crumbs="crumbs" class="pb-4 my-2"></breadcrumb>

                <!-- Is Loading -->
                <div v-if="isLoading" class="text-center">
                    <spinner></spinner>
                </div>
                <!-- / Is Loading -->

                <!-- Integrations Grid -->
                <div v-if="!isLoading" class="row">
                    <!-- Sage Integration Tile -->
                     <div class="col-lg-6 col-md-12 mb-4">
                        <div class="card h-100 border-0 shadow-sm">
                            <div class="card-body p-4">
                                <div class="d-flex align-items-center mb-3">
                                    <div class="flex-shrink-0 me-3">
                                        <div class="rounded-3" style="width: 48px; height: 48px; overflow: hidden;">
                                            <img src="/img/sage.png" alt="Sage" style="width: 100%; height: 100%; object-fit: cover;">
                                        </div>
                                    </div>
                                    <div class="flex-grow-1">
                                        <h5 class="mb-1">Sage Accounting</h5>
                                        <p class="text-muted mb-0 fs-sm">Connect your Sage Accounting account</p>
                                    </div>
                                </div>
                                
                                <p class="text-muted mb-4">
                                    Sync to Sage to import your clients for quickly making referrals and managing your client relationships.
                                </p>

                                <div class="d-flex justify-content-between align-items-center">
                                    <div class="d-flex align-items-center">
                                        <span class="badge bg-success bg-opacity-10 text-success me-2">
                                            <i class="fi-check-circle me-1"></i>
                                            Available
                                        </span>
                                    </div>
                                    <router-link 
                                        to="/integrations/sage" 
                                        class="btn btn-primary btn-sm"
                                    >
                                        Configure
                                    </router-link>
                                </div>
                            </div>
                        </div>
                    </div> 
                    <!-- / Sage Integration Tile -->

                    <!-- Webhooks Integration Tile -->
                    <div class="col-lg-6 col-md-12 mb-4">
                        <div class="card h-100 border-0 shadow-sm">
                            <div class="card-body p-4">
                                <div class="d-flex align-items-center mb-3">
                                    <div class="flex-shrink-0 me-3">
                                        <div class="bg-primary bg-opacity-10 rounded-3 p-3">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#007bff"/>
                                                <path d="M2 17L12 22L22 17" stroke="#007bff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                                <path d="M2 12L12 17L22 12" stroke="#007bff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                            </svg>
                                        </div>
                                    </div>
                                    <div class="flex-grow-1">
                                        <h5 class="mb-1">Webhooks</h5>
                                        <p class="text-muted mb-0 fs-sm">Connect to Zapier and other services</p>
                                    </div>
                                </div>
                                
                                <p class="text-muted mb-4">
                                    Set up webhook subscriptions to receive real-time notifications when jobs and quotes are created or updated.
                                </p>

                                <div class="d-flex justify-content-between align-items-center">
                                    <div class="d-flex align-items-center">
                                        <span class="badge bg-success bg-opacity-10 text-success me-2">
                                            <i class="fi-check-circle me-1"></i>
                                            Available
                                        </span>
                                    </div>
                                    <router-link 
                                        to="/integrations/webhooks" 
                                        class="btn btn-primary btn-sm"
                                    >
                                        Configure
                                    </router-link>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- / Webhooks Integration Tile -->

                    <!-- Coming Soon Tile -->
                    <div class="col-lg-6 col-md-12 mb-4">
                        <div class="card h-100 border-0 shadow-sm opacity-75">
                            <div class="card-body p-4">
                                <div class="d-flex align-items-center mb-3">
                                    <div class="flex-shrink-0 me-3">
                                        <div class="bg-secondary bg-opacity-10 rounded-3 p-3">
                                            <i class="fi-clock text-secondary fs-4"></i>
                                        </div>
                                    </div>
                                    <div class="flex-grow-1">
                                        <h5 class="mb-1">More Integrations</h5>
                                        <p class="text-muted mb-0 fs-sm">Coming soon</p>
                                    </div>
                                </div>
                                
                                <p class="text-muted mb-4">
                                    We're working on more integrations to help streamline your workflow.
                                </p>

                                <div class="d-flex justify-content-between align-items-center">
                                    <div class="d-flex align-items-center">
                                        <span class="badge bg-secondary bg-opacity-10 text-secondary me-2">
                                            <i class="fi-clock me-1"></i>
                                            Coming Soon
                                        </span>
                                    </div>
                                    <button class="btn btn-secondary btn-sm" disabled>
                                        Not Available
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- / Coming Soon Tile -->
                </div>
                <!-- / Integrations Grid -->

            </div>
        </section>
        <!-- / Integrations Content -->
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Spinner from '../components/Spinner.vue';
import Breadcrumb from '../components/Breadcrumb.vue';

export default {
    name: 'Integrations',
    
    components: { 
        Spinner, 
        Breadcrumb 
    },

    data() {
        return {
            isLoading: false
        }
    },

    computed: {
        ...mapGetters('auth', {
            user: 'user',
            userRole: 'userRole',
            isAdvisor: 'isAdvisor'
        }),

        crumbs() {
            return [
                {
                    href: "/",
                    label: "Dashboard",
                },
                {
                    href: "#",
                    label: "Integrations",
                    active: true,
                },
            ];
        }
    },

    created() {
        // Check if user is an advisor, if not redirect
        if (!this.isAdvisor) {
            this.$router.push('/');
        }
    },

    methods: {
        // Methods can be added here as needed
    }
}
</script>

<style scoped>
.card {
    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.card:hover {
    transform: translateY(-2px);
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

.card.opacity-75:hover {
    transform: none;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
}
</style> 